// src/pages/BDPipeline.js — Kanban board for BD mandate pipeline
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import api from '../lib/api'

// ── Constants ─────────────────────────────────────────────────────────────

const STAGES = [
  'REVIEWING', 'IN DISCUSSIONS', 'NEGOTIATING EL',
  'LIVE', 'ON HOLD', 'PASSED',
]

const STATUSES      = ['Active', 'On Hold', 'Closed', 'Passed']
const PRODUCT_TYPES = ['Fund', 'Secondaries', 'Co-Invest', 'Private Placement', 'Advisory']

const STAGE_BADGE = {
  'REVIEWING':      'badge-gray',
  'IN DISCUSSIONS': 'badge-blue',
  'NEGOTIATING EL': 'badge-yellow',
  'LIVE':           'badge-green',
  'ON HOLD':        'badge-yellow',
  'PASSED':         'badge-gray',
}

// Top-border accent on each column header
const STAGE_ACCENT = {
  'REVIEWING':      '#4b5563',
  'IN DISCUSSIONS': '#3b82f6',
  'NEGOTIATING EL': '#f59e0b',
  'LIVE':           '#10b981',
  'ON HOLD':        '#f59e0b',
  'PASSED':         '#6b7280',
}

// ── Helpers ───────────────────────────────────────────────────────────────

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatRaise(amount) {
  if (amount == null) return null
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000)     return `$${Math.round(amount / 1_000_000)}M`
  return `$${Number(amount).toLocaleString()}`
}

function ownerLabel(uuid) {
  if (!uuid) return null
  return uuid.slice(0, 8) + '…'
}

// ── Stage dropdown (inline badge click → move to stage) ───────────────────

function StageMenu({ current, onSelect, onClose }) {
  const ref = useRef()

  useEffect(() => {
    function handleDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleDown)
    return () => document.removeEventListener('mousedown', handleDown)
  }, [onClose])

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        zIndex: 200,
        background: '#1a2235',
        border: '1px solid var(--border-light)',
        borderRadius: '6px',
        padding: '4px',
        minWidth: '170px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
      }}
    >
      {STAGES.map(s => {
        const isCurrent = s === current
        return (
          <button
            key={s}
            disabled={isCurrent}
            onClick={e => { e.stopPropagation(); e.preventDefault(); onSelect(s) }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              background: isCurrent ? 'var(--accent-muted)' : 'transparent',
              border: 'none',
              borderRadius: '4px',
              padding: '7px 10px',
              cursor: isCurrent ? 'default' : 'pointer',
              color: isCurrent ? '#60a5fa' : 'var(--text-primary)',
              fontSize: '12px',
              fontWeight: isCurrent ? '600' : '400',
              fontFamily: 'inherit',
              textAlign: 'left',
            }}
          >
            <span style={{ width: '12px', flexShrink: 0, fontSize: '10px' }}>
              {isCurrent ? '✓' : ''}
            </span>
            {s}
          </button>
        )
      })}
    </div>
  )
}

// ── Mandate card ──────────────────────────────────────────────────────────

function MandateCard({ mandate, onStageUpdate, onDelete }) {
  const [stageMenuOpen, setStageMenuOpen] = useState(false)

  const raise    = formatRaise(mandate.target_raise)
  const owner    = ownerLabel(mandate.relationship_owner)
  const badgeCls = STAGE_BADGE[mandate.bd_stage] ?? 'badge-gray'
  const isPartnered = mandate.coverage_type === 'Partnered'

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        padding: '13px 14px',
        transition: 'border-color 0.12s, background 0.12s',
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--border-light)'
        e.currentTarget.style.background  = 'var(--surface-raised)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = ''
        e.currentTarget.style.background  = ''
      }}
    >
      {/* Name + GP firm — link to detail */}
      <Link
        to={`/mandates/${mandate.mandate_id}`}
        style={{ display: 'block', textDecoration: 'none', marginBottom: '10px' }}
      >
        <div style={{
          fontWeight: '600',
          fontSize: '13px',
          color: 'var(--text-primary)',
          lineHeight: '1.3',
          marginBottom: '3px',
        }}>
          {mandate.mandate_name}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
          {mandate.gp_firm?.name ?? '—'}
          {mandate.partner_firm && (
            <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>
              · {mandate.partner_firm.name}
            </span>
          )}
        </div>
      </Link>

      {/* Badges row */}
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {/* Stage badge — click to open stage menu */}
        <div style={{ position: 'relative' }}>
          <button
            className={`badge ${badgeCls}`}
            onClick={e => { e.preventDefault(); e.stopPropagation(); setStageMenuOpen(v => !v) }}
            title="Change stage"
            style={{
              cursor: 'pointer',
              border: 'none',
              fontFamily: 'inherit',
              outline: 'none',
            }}
          >
            {mandate.bd_stage}
          </button>
          {stageMenuOpen && (
            <StageMenu
              current={mandate.bd_stage}
              onClose={() => setStageMenuOpen(false)}
              onSelect={newStage => {
                setStageMenuOpen(false)
                onStageUpdate(mandate.mandate_id, newStage)
              }}
            />
          )}
        </div>

        {/* Coverage type */}
        <span className={`badge ${isPartnered ? 'badge-purple' : 'badge-gray'}`}>
          {mandate.coverage_type ?? 'Solo'}
        </span>

        {/* Product types (each as a tiny gray badge) */}
        {mandate.product_type?.map(pt => (
          <span key={pt} className="badge badge-gray" style={{ fontSize: '10px', opacity: 0.7 }}>
            {pt}
          </span>
        ))}
      </div>

      {/* Footer: raise + owner + delete */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        {raise ? (
          <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)' }}>
            {raise}
          </span>
        ) : <span />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {owner && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
              {owner}
            </span>
          )}
          <button
            onClick={e => { e.stopPropagation(); e.preventDefault(); onDelete(mandate) }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', padding: '2px 4px', borderRadius: 4,
              display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            title="Delete mandate"
          ><TrashIcon /></button>
        </div>
      </div>
    </div>
  )
}

// ── Kanban column ─────────────────────────────────────────────────────────

function KanbanColumn({ stage, mandates, onStageUpdate, onDelete }) {
  const accent = STAGE_ACCENT[stage]

  return (
    <div style={{ minWidth: '260px', width: '260px', flexShrink: 0 }}>
      {/* Column header */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderBottom: 'none',
        borderRadius: '8px 8px 0 0',
        borderTop: `3px solid ${accent}`,
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{
          fontSize: '11px',
          fontWeight: '700',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
        }}>
          {stage}
        </span>
        <span
          className="badge badge-gray"
          style={{ fontSize: '10px', minWidth: '20px', justifyContent: 'center' }}
        >
          {mandates.length}
        </span>
      </div>

      {/* Cards container with drop zone */}
      <Droppable droppableId={stage}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              background: snapshot.isDraggingOver ? '#111d30' : '#0d1321',
              border: '1px solid var(--border)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '7px',
              minHeight: '120px',
              transition: 'background 0.15s',
            }}
          >
            {mandates.length === 0 && !snapshot.isDraggingOver && (
              <div style={{
                padding: '20px 8px',
                textAlign: 'center',
                fontSize: '11px',
                color: 'var(--text-muted)',
                fontStyle: 'italic',
              }}>
                No mandates
              </div>
            )}
            {mandates.map((m, index) => (
              <Draggable key={m.mandate_id} draggableId={m.mandate_id} index={index}>
                {(drag, dragSnapshot) => (
                  <div
                    ref={drag.innerRef}
                    {...drag.draggableProps}
                    {...drag.dragHandleProps}
                    style={{
                      borderRadius: 6,
                      opacity: dragSnapshot.isDragging ? 0.92 : 1,
                      boxShadow: dragSnapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.5)' : 'none',
                      ...drag.draggableProps.style,
                    }}
                  >
                    <MandateCard
                      mandate={m}
                      onStageUpdate={onStageUpdate}
                      onDelete={onDelete}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  )
}

// ── New Mandate modal ─────────────────────────────────────────────────────

const EMPTY_FORM = {
  mandate_name:    '',
  gp_firm_id:      '',
  bd_stage:        'REVIEWING',
  coverage_type:   'Solo',
  partner_firm_id: '',
  product_type:    [],
  target_raise:    '',
  status:          'Active',
}

function NewMandateModal({ firms, onClose, onCreate }) {
  const [form, setForm]       = useState(EMPTY_FORM)
  const [submitting, setSub]  = useState(false)
  const [error, setError]     = useState(null)

  const gpFirms      = firms.filter(f => f.firm_type?.includes('GP Client'))
  const partnerFirms = firms.filter(f => f.firm_type?.includes('Partner Advisor'))

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }))
    setError(null)
  }

  function toggleProduct(type) {
    setForm(prev => ({
      ...prev,
      product_type: prev.product_type.includes(type)
        ? prev.product_type.filter(t => t !== type)
        : [...prev.product_type, type],
    }))
  }

  // Close on overlay click
  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.mandate_name.trim()) return setError('Mandate name is required.')
    if (!form.gp_firm_id)          return setError('GP Firm is required.')

    setSub(true)
    setError(null)

    const payload = {
      mandate_name:  form.mandate_name.trim(),
      gp_firm_id:    form.gp_firm_id,
      bd_stage:      form.bd_stage,
      coverage_type: form.coverage_type,
      status:        form.status,
    }
    if (form.product_type.length)                              payload.product_type    = form.product_type
    if (form.target_raise !== '')                              payload.target_raise    = parseFloat(form.target_raise)
    if (form.coverage_type === 'Partnered' && form.partner_firm_id) payload.partner_firm_id = form.partner_firm_id

    try {
      await onCreate(payload)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create mandate.')
    } finally {
      setSub(false)
    }
  }

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
    >
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border-light)',
        borderRadius: '10px',
        width: '100%',
        maxWidth: '520px',
        maxHeight: '92vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: 'var(--surface)',
          zIndex: 1,
        }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600' }}>New Mandate</h2>
          <button
            className="btn-icon"
            onClick={onClose}
            style={{ fontSize: '16px', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {error && (
            <div style={{
              background: 'var(--danger-muted)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: '6px',
              padding: '10px 14px',
              fontSize: '13px',
              color: '#f87171',
            }}>
              {error}
            </div>
          )}

          {/* Mandate name */}
          <div className="input-group">
            <label className="input-label">Mandate Name *</label>
            <input
              className="input"
              value={form.mandate_name}
              onChange={e => set('mandate_name', e.target.value)}
              placeholder="e.g. Meridian Fund IV"
              autoFocus
            />
          </div>

          {/* GP Firm */}
          <div className="input-group">
            <label className="input-label">GP Firm *</label>
            <select
              className="select"
              value={form.gp_firm_id}
              onChange={e => set('gp_firm_id', e.target.value)}
            >
              <option value="">Select GP firm…</option>
              {gpFirms.map(f => (
                <option key={f.firm_id} value={f.firm_id}>{f.name}</option>
              ))}
            </select>
            {gpFirms.length === 0 && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                No GP Client firms found. Add a firm with type "GP Client" first.
              </div>
            )}
          </div>

          {/* BD Stage + Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="input-group">
              <label className="input-label">BD Stage</label>
              <select className="select" value={form.bd_stage} onChange={e => set('bd_stage', e.target.value)}>
                {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Status</label>
              <select className="select" value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Coverage Type */}
          <div className="input-group">
            <label className="input-label">Coverage Type</label>
            <div style={{ display: 'flex', gap: '20px', marginTop: '6px' }}>
              {['Solo', 'Partnered'].map(t => (
                <label
                  key={t}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: form.coverage_type === t ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: form.coverage_type === t ? '500' : '400',
                  }}
                >
                  <input
                    type="radio"
                    name="coverage_type"
                    value={t}
                    checked={form.coverage_type === t}
                    onChange={() => set('coverage_type', t)}
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>

          {/* Partner Firm — only shown when Partnered */}
          {form.coverage_type === 'Partnered' && (
            <div className="input-group">
              <label className="input-label">Partner Firm</label>
              <select
                className="select"
                value={form.partner_firm_id}
                onChange={e => set('partner_firm_id', e.target.value)}
              >
                <option value="">Select partner firm…</option>
                {partnerFirms.map(f => (
                  <option key={f.firm_id} value={f.firm_id}>{f.name}</option>
                ))}
              </select>
              {partnerFirms.length === 0 && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  No Partner Advisor firms found.
                </div>
              )}
            </div>
          )}

          {/* Product Type (checkboxes) */}
          <div className="input-group">
            <label className="input-label">Product Type</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px',
              marginTop: '6px',
            }}>
              {PRODUCT_TYPES.map(t => {
                const checked = form.product_type.includes(t)
                return (
                  <label
                    key={t}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: checked ? 'var(--text-primary)' : 'var(--text-secondary)',
                      padding: '6px 10px',
                      borderRadius: '5px',
                      border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                      background: checked ? 'var(--accent-muted)' : 'transparent',
                      transition: 'all 0.12s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProduct(t)}
                      style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    />
                    {t}
                  </label>
                )
              })}
            </div>
          </div>

          {/* Target Raise */}
          <div className="input-group">
            <label className="input-label">Target Raise (USD)</label>
            <input
              className="input"
              type="number"
              value={form.target_raise}
              onChange={e => set('target_raise', e.target.value)}
              placeholder="e.g. 500000000"
              min="0"
              step="1000000"
            />
            {form.target_raise !== '' && (
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '3px' }}>
                = {formatRaise(parseFloat(form.target_raise))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            paddingTop: '4px',
            borderTop: '1px solid var(--border)',
            marginTop: '4px',
          }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting
                ? <><span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Creating…</>
                : 'Create Mandate'
              }
            </button>
          </div>

        </form>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function BDPipeline() {
  const [modalOpen, setModalOpen] = useState(false)
  const [toDelete, setToDelete]   = useState(null)
  const queryClient = useQueryClient()

  // ── Fetch all mandates
  const { data: mandatesData, isLoading: mandatesLoading, error: mandatesError } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
  })

  // ── Fetch all firms (for modal dropdowns — lazy, not blocking)
  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
    enabled: modalOpen,   // only fetch firms when modal is opened
  })

  // ── Mutation: update bd_stage (with optimistic update for instant drag feedback)
  const stageMutation = useMutation({
    mutationFn: ({ id, bd_stage }) =>
      api.patch(`/api/mandates/${id}`, { bd_stage }).then(r => r.data),
    onMutate: async ({ id, bd_stage }) => {
      await queryClient.cancelQueries({ queryKey: ['mandates'] })
      const previous = queryClient.getQueryData(['mandates'])
      queryClient.setQueryData(['mandates'], old =>
        (old ?? []).map(m => m.mandate_id === id ? { ...m, bd_stage } : m)
      )
      return { previous }
    },
    onError: (err, _vars, ctx) => {
      console.error('[BDPipeline] stage update failed:', err.message)
      if (ctx?.previous) queryClient.setQueryData(['mandates'], ctx.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['mandates'] }),
  })

  function handleDragEnd(result) {
    const { destination, source, draggableId } = result
    if (!destination) return
    if (destination.droppableId === source.droppableId && destination.index === source.index) return
    stageMutation.mutate({ id: draggableId, bd_stage: destination.droppableId })
  }

  // ── Mutation: delete mandate
  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/mandates/${id}`).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mandates'] })
      setToDelete(null)
    },
  })

  // ── Mutation: create mandate
  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/api/mandates', payload).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mandates'] }),
  })

  const mandates = mandatesData ?? []
  const firms    = firmsData    ?? []

  // Group by bd_stage — preserves STAGES order
  const grouped = Object.fromEntries(
    STAGES.map(stage => [stage, mandates.filter(m => m.bd_stage === stage)])
  )

  const totalActive = mandates.filter(m => m.status === 'Active').length

  return (
    <div className="pipeline-page">

      {/* ── Page header ──────────────────────────────────────────── */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div className="page-header-text">
          <h1>BD Pipeline</h1>
          <p>
            {mandatesLoading
              ? 'Loading mandates…'
              : `${mandates.length} mandate${mandates.length !== 1 ? 's' : ''} · ${totalActive} active`
            }
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
          + New Mandate
        </button>
      </div>

      {/* ── Board ────────────────────────────────────────────────── */}
      {mandatesLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '300px' }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : mandatesError ? (
        <div style={{
          background: 'var(--danger-muted)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: '8px',
          padding: '20px 24px',
          color: '#f87171',
          fontSize: '14px',
        }}>
          Failed to load mandates: {mandatesError.message}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div style={{
            display: 'flex',
            gap: '12px',
            overflowX: 'auto',
            overflowY: 'visible',
            paddingBottom: '32px',
            alignItems: 'flex-start',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--border-light) transparent',
          }}>
            {STAGES.map(stage => (
              <KanbanColumn
                key={stage}
                stage={stage}
                mandates={grouped[stage]}
                onStageUpdate={(id, bd_stage) => stageMutation.mutate({ id, bd_stage })}
                onDelete={m => setToDelete(m)}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      {/* ── New mandate modal ─────────────────────────────────────── */}
      {modalOpen && (
        <NewMandateModal
          firms={firms}
          onClose={() => setModalOpen(false)}
          onCreate={createMutation.mutateAsync}
        />
      )}

      {/* ── Delete confirm ────────────────────────────────────────── */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.mandate_name}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.mandate_id)}
          deleting={deleteMutation.isPending}
        />
      )}

    </div>
  )
}
